import axios from "axios";
import { API_BASE_URL } from "../apiConfig";

const normalizeBaseUrl = (value = "") =>
  String(value || "")
    .trim()
    .replace(/\/+$/, "");

const configuredBaseUrl = normalizeBaseUrl(
  process.env.REACT_APP_SCADENTAR_FURNIZORI_API_BASE_URL
);
const BASE_URL = configuredBaseUrl || API_BASE_URL;

const api = axios.create({
  baseURL: `${BASE_URL}/api/scadentar-furnizori`,
  timeout: 25000,
});

export const getInvoices = (params) => api.get("/invoices", { params });
export const getInvoicesSummary = (params) =>
  api.get("/invoices/summary", { params });
export const downloadInvoicesZip = (params) =>
  api.get("/invoices/download-zip", {
    params,
    responseType: "blob",
    timeout: 180000,
  });
export const createInvoice = (payload) => api.post("/invoices", payload);
export const aiAutoCreateInvoiceFromPdf = async ({ file, ...meta }) => {
  const formData = new FormData();
  if (file) formData.append("file", file);
  Object.entries(meta || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null)
      formData.append(key, String(value));
  });
  return api.post("/invoices/ai-auto-from-pdf", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 120000,
  });
};
export const updateInvoice = (id, payload) =>
  api.put(`/invoices/${id}`, payload);
export const closeInvoice = (id, payload = {}) =>
  api.post(`/invoices/${id}/close`, payload);
export const setInvoiceStatusManual = (id, payload = {}) =>
  api.post(`/invoices/${id}/status-manual`, payload || {});
export const getInvoiceStatusAudit = (id, params = {}) =>
  api.get(`/invoices/${id}/status-audit`, { params });

export const getInvoiceProofUrl = (invoiceId) =>
  `${BASE_URL}/api/scadentar-furnizori/invoices/${encodeURIComponent(String(invoiceId || "").trim())}/proof`;
export const deleteInvoice = (id) => api.delete(`/invoices/${id}`);

export const getPayments = (invoiceId) =>
  api.get(`/invoices/${invoiceId}/payments`);
export const getInvoiceLines = (invoiceId) =>
  api.get(`/invoices/${invoiceId}/lines`);
export const createPayment = (invoiceId, payload) =>
  api.post(`/invoices/${invoiceId}/payments`, payload);
export const deletePayment = (paymentId) =>
  api.delete(`/payments/${paymentId}`);

export const getRecurrences = () => api.get("/recurrences");
export const createRecurrence = (payload) => api.post("/recurrences", payload);
export const updateRecurrence = (id, payload) =>
  api.put(`/recurrences/${id}`, payload);
export const generateRecurrenceNow = (id) =>
  api.post(`/recurrences/${id}/generate-now`);
export const runRecurrenceJob = () => api.post("/recurrences/run-job");
export const predictCurrentMonthRecurrences = () =>
  api.post("/recurrences/predict-current-month");

export const getFxRate = (params) => api.get("/fx/rate", { params });
export const refreshFxRates = () => api.post("/fx/refresh");
export const convertFx = (payload) => api.post("/fx/convert", payload || {});

export const runSpvBridgeImport = (payload) =>
  api.post("/imports/spv/run", payload || {}, { timeout: 180000 });
export const importInvoicesFromXmlZip = async (file, options = {}) => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post("/imports/spv/xml-zip", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 180000,
    onUploadProgress: options.onUploadProgress,
  });
};

export const getSpvBridgeLogs = () => api.get("/imports/spv/logs");
export const getSpvStatus = () => api.get("/imports/spv/status");
export const getSpvDocumentUrl = (externalId, params = {}) => {
  const query = new URLSearchParams({
    redirect: "1",
    ...(params || {}),
  }).toString();
  const safeId = encodeURIComponent(String(externalId || "").trim());
  return `${BASE_URL}/api/scadentar-furnizori/imports/spv/document/${safeId}?${query}`;
};

export const generateInvoiceFromSpvXml = async (externalId, payload = {}) => {
  const external_id = String(externalId || "").trim();
  const body = { ...(payload || {}), external_id };
  const t0 = Date.now();
  console.info("[SF][frontend][generate-xml] request", {
    url: "/imports/spv/document/generate-invoice",
    external_id,
    invoice_id: body?.invoice_id || null,
  });
  try {
    const response = await api.post(
      "/imports/spv/document/generate-invoice",
      body,
      {
        timeout: 120000,
      },
    );
    console.info("[SF][frontend][generate-xml] response", {
      ms: Date.now() - t0,
      status: response?.status,
      action: response?.data?.action || null,
      source: response?.data?.source || null,
      invoice_id: response?.data?.invoice?.id || null,
    });
    return response;
  } catch (error) {
    console.error("[SF][frontend][generate-xml] error", {
      ms: Date.now() - t0,
      message: error?.message,
      status: error?.response?.status,
      data: error?.response?.data || null,
    });
    throw error;
  }
};

export const getVendors = (params) => api.get("/vendors", { params });
export const createVendor = (payload) => api.post("/vendors", payload);
export const autodetectVendorByCui = (payload, options = {}) =>
  api.post("/vendors/autodetect-cui", payload || {}, {
    params: { include_prompt: options.includePrompt ? 1 : 0 },
    timeout: options.timeout || 45000,
  });
export const getVendor = (id) => api.get(`/vendors/${id}`);
export const getVendorLogoUrl = (id) =>
  `${BASE_URL}/api/scadentar-furnizori/vendors/${encodeURIComponent(String(id))}/logo`;
export const updateVendor = (id, payload) => api.put(`/vendors/${id}`, payload);
export const deleteVendor = (id) => api.delete(`/vendors/${id}`);
export const getVendorSummary = (id) => api.get(`/vendors/${id}/summary`);
export const getVendorInvoices = (id) => api.get(`/vendors/${id}/invoices`);
export const getVendorDocuments = (id) => api.get(`/vendors/${id}/documents`);
export const getVendorDocumentFileUrl = (id, docId) =>
  `${BASE_URL}/api/scadentar-furnizori/vendors/${encodeURIComponent(String(id))}/documents/${encodeURIComponent(String(docId))}/file`;
export const addVendorDocument = (id, payload) =>
  api.post(`/vendors/${id}/documents`, payload);
export const reuploadVendorDocument = (id, docId, payload) =>
  api.put(`/vendors/${id}/documents/${docId}/reupload`, payload || {});
export const deleteVendorDocument = (id, docId) =>
  api.delete(`/vendors/${id}/documents/${docId}`);
export const getVendorNotes = (id) => api.get(`/vendors/${id}/notes`);
export const addVendorNote = (id, payload) =>
  api.post(`/vendors/${id}/notes`, payload);
export const uploadVendorDocument = async (id, { file, ...meta }) => {
  const formData = new FormData();
  if (file) formData.append("file", file);
  Object.entries(meta || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null)
      formData.append(key, String(value));
  });
  return api.post(`/vendors/${id}/documents`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};
export const reuploadVendorDocumentFile = async (
  id,
  docId,
  { file, ...meta },
) => {
  const formData = new FormData();
  if (file) formData.append("file", file);
  Object.entries(meta || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null)
      formData.append(key, String(value));
  });
  return api.put(`/vendors/${id}/documents/${docId}/reupload`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};
export const autoFetchVendorLogo = (id, payload) =>
  api.post(`/vendors/${id}/logo/autofetch`, payload || {});
export const uploadVendorLogoFromUrl = (id, payload) =>
  api.post(`/vendors/${id}/logo/from-url`, payload || {});
export const backfillVendorLinks = () => api.post("/vendors/backfill-invoices");

export const validateInvoicePdfAgainstDb = async (invoiceId, file) => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post(`/invoices/${invoiceId}/validate-pdf`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 120000,
  });
};

export const uploadDocument = async (file, meta = {}) => {
  const formData = new FormData();
  formData.append("file", file);
  Object.entries(meta || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      formData.append(key, String(value));
    }
  });
  const response = await api.post("/uploads", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response;
};

export default api;
